import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, Send } from 'lucide-react';
import SEO from './components/SEO';
import {
  StatsSection,
  ServicesSection,
  GallerySection,
  TestimonialsSection,
  ContactSection
} from './components/home';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    name: '', email: '', phone: '', event_type: 'Mariage',
    event_date: '', location: '', guest_count: '', budget: '', description: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ quote: '' });
  const [galleryItems, setGalleryItems] = useState([]);
  const [homeServices, setHomeServices] = useState([]);
  const [backgroundVideo, setBackgroundVideo] = useState('');
  const [homepageHeroVideo, setHomepageHeroVideo] = useState('');
  const [isBackgroundVideoLoaded, setIsBackgroundVideoLoaded] = useState(false);
  const [stats, setStats] = useState([
    { value: '300+', label: 'Spectacles de pyrotechnie réalisés' },
    { value: '500+', label: 'Références produits de fabricants renommés' },
    { value: '175 000+', label: 'Sourires donnés' },
    { value: '20+', label: 'Artificiers passionnés' }
  ]);

  // Defer background video loading for better initial performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsBackgroundVideoLoaded(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials?approved_only=true`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des témoignages:', error);
    }
  }, []);

  const fetchSiteMedia = useCallback(async () => {
    try {
      const mediaResponse = await axios.get(`${API}/site-media`);
      const mediaItems = mediaResponse.data;
      
      const bgVideo = mediaItems.find(m => m.key === 'background_video');
      if (bgVideo?.url) setBackgroundVideo(bgVideo.url);
      
      const heroVideo = mediaItems.find(m => m.key === 'homepage_hero_video');
      if (heroVideo?.url) setHomepageHeroVideo(heroVideo.url);

      const statsData = [
        mediaItems.find(m => m.key === 'stat_spectacles'),
        mediaItems.find(m => m.key === 'stat_produits'),
        mediaItems.find(m => m.key === 'stat_sourires'),
        mediaItems.find(m => m.key === 'stat_artificiers')
      ].filter(Boolean);

      if (statsData.length > 0) {
        setStats(statsData.map(s => ({ value: s.value, label: s.label })));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des médias:', error);
    }
  }, []);

  const fetchHomeServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/home-services`);
      setHomeServices(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
    }
  }, []);

  const extractYouTubeId = (url) => {
    if (!url) return null;
    let videoId = null;
    
    if (url.includes('/embed/')) {
      videoId = url.split('/embed/')[1]?.split('?')[0];
    } else if (url.includes('watch?v=')) {
      videoId = url.split('watch?v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/v/')) {
      videoId = url.split('youtube.com/v/')[1]?.split('?')[0];
    }
    
    return videoId;
  };

  const getEmbedUrl = (url) => {
    if (url && url.includes('/embed/') && url.includes('autoplay=1')) return url;
    
    const videoId = extractYouTubeId(url);
    if (!videoId) return url;
    
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&iv_load_policy=3`;
  };

  const convertYouTubeUrl = (url) => {
    if (!url) return url;
    const videoId = extractYouTubeId(url);
    
    if (videoId) {
      const params = [
        'autoplay=0', 'rel=0', 'modestbranding=1', 'iv_load_policy=3',
        'controls=1', 'playsinline=1', 'enablejsapi=1',
        'origin=' + window.location.origin,
        'widget_referrer=' + window.location.origin
      ].join('&');
      
      return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
    }
    
    return url;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = extractYouTubeId(url);
    if (videoId) return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    return null;
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      const processedItems = response.data.map(item => ({
        ...item,
        url: item.type === 'video' ? convertYouTubeUrl(item.url) : item.url
      }));
      setGalleryItems(processedItems);
    } catch (error) {
      console.error('Erreur lors du chargement de la galerie:', error);
    }
  };

  useEffect(() => {
    fetchTestimonials();
    fetchGallery();
    fetchHomeServices();
    fetchSiteMedia();
  }, []);

  // Gérer le scroll automatique depuis les pages de services
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
      // Nettoyer le state pour éviter les scrolls répétés
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convertir les chaînes vides en null pour les champs optionnels
      const sanitizedForm = {
        ...quoteForm,
        guest_count: quoteForm.guest_count === '' ? null : parseInt(quoteForm.guest_count),
        budget: quoteForm.budget || null
      };
      await axios.post(`${API}/quotes`, sanitizedForm);
      setSubmitStatus({ quote: 'Votre demande de devis a été envoyée! Nous vous contacterons bientôt.' });
      setQuoteForm({
        name: '', email: '', phone: '', event_type: 'Mariage',
        event_date: '', location: '', guest_count: '', budget: '', description: ''
      });
      setTimeout(() => setSubmitStatus({ quote: '' }), 5000);
    } catch (error) {
      console.error('Erreur soumission devis:', error);
      setSubmitStatus({ quote: 'Erreur lors de l\'envoi. Veuillez réessayer.' });
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <SEO 
        title="CYGA EVENEMENT - Spectacles Pyrotechniques de Prestige | Excellence & Innovation"
        description="Maîtres artificiers depuis 30 ans. Créations pyrotechniques d'exception pour événements prestigieux : mariages haut de gamme, festivités municipales, SFX cinématographiques. Solutions jour et nuit. +300 spectacles d'excellence en France."
        keywords="spectacle pyrotechnique prestige, feux d'artifice luxe, artificier expert certifié, pyrotechnie haut de gamme, effets spéciaux professionnels, pyromusicale prestige, artifices de jour, fumigènes professionnels, SFX cinéma, pyrotechnie scène, spectacle transportable premium"
        url="https://www.cyga-evenement.fr"
        type="website"
      />
      <div className="app" data-testid="cyga-website">
        {/* Background Video - Deferred loading for performance */}
        <div className="page-video-background">
          {isBackgroundVideoLoaded ? (
            <iframe
              className="page-background-video"
              src={getEmbedUrl(backgroundVideo) || "https://www.youtube.com/embed/AcQHHKsBFyE?autoplay=1&mute=1&loop=1&playlist=AcQHHKsBFyE&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&iv_load_policy=3"}
              title="CYGA EVENEMENT - Spectacle Pyrotechnique de fond"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              loading="lazy"
            ></iframe>
          ) : (
            <div className="video-placeholder-bg"></div>
          )}
        </div>

        {/* Navigation */}
        <nav className="nav" data-testid="main-navigation">
          <div className="nav-container">
            <div className="nav-logo" data-testid="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_cyga-refresh/artifacts/iagmxpji_Cyga%20events%20Visuel%20Site-02.png" 
                alt="Cyga Events Logo" 
                className="logo-icon-img"
                width="54"
                height="52"
                loading="eager"
                fetchpriority="high"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_cyga-refresh/artifacts/pw7229o4_Cyga%20events%20Visuel%20Site-04.png" 
                alt="Cyga Events" 
                className="logo-text-img"
                width="143"
                height="44"
                loading="eager"
                fetchpriority="high"
              />
            </div>
            
            <div className="nav-links-desktop">
              <button onClick={() => scrollToSection('accueil')} data-testid="nav-accueil">Accueil</button>
              <button onClick={() => scrollToSection('services')} data-testid="nav-services">Services</button>
              <button onClick={() => scrollToSection('galerie')} data-testid="nav-galerie">Galerie</button>
              <button onClick={() => scrollToSection('temoignages')} data-testid="nav-temoignages">Témoignages</button>
              <button onClick={() => scrollToSection('devis')} data-testid="nav-devis">Devis</button>
              <button onClick={() => scrollToSection('contact')} className="nav-cta" data-testid="nav-contact">Contact</button>
            </div>

            <button className="nav-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="nav-mobile-menu" data-testid="mobile-menu">
              <button onClick={() => scrollToSection('accueil')}>Accueil</button>
              <button onClick={() => scrollToSection('services')}>Services</button>
              <button onClick={() => scrollToSection('galerie')}>Galerie</button>
              <button onClick={() => scrollToSection('temoignages')}>Témoignages</button>
              <button onClick={() => scrollToSection('devis')}>Devis</button>
              <button onClick={() => scrollToSection('contact')}>Contact</button>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section id="accueil" className="hero" data-testid="hero-section">
          <div className="hero-video-container">
            <iframe
              className="hero-video"
              src={getEmbedUrl(homepageHeroVideo) || "https://www.youtube.com/embed/AcQHHKsBFyE?autoplay=1&mute=1&loop=1&playlist=AcQHHKsBFyE&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&iv_load_policy=3"}
              title="CYGA EVENEMENT - Spectacle Pyrotechnique Hero"
              frameBorder="0"
              allow="autoplay; encrypted-media"
            ></iframe>
          </div>
          <div className="hero-overlay">
            <div className="hero-content">
              <h1 className="hero-title" data-testid="hero-title">
                CRÉATEUR DE<br/>
                <span className="hero-highlight">FRISSONS</span>
              </h1>
              <p className="hero-subtitle" data-testid="hero-subtitle">
                Plus de 30 ans d'expérience dans l'artifice pour du spectacle unique et inoubliable.
              </p>
              <button onClick={() => scrollToSection('devis')} className="hero-cta" data-testid="hero-cta-button">
                Demander un Devis
              </button>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <StatsSection stats={stats} />

        {/* Services Section */}
        <ServicesSection homeServices={homeServices} />

        {/* Gallery Section */}
        <GallerySection 
          galleryItems={galleryItems} 
          getYouTubeThumbnail={getYouTubeThumbnail} 
        />

        {/* Testimonials Section */}
        <TestimonialsSection testimonials={testimonials} />

        {/* Quote Request Section */}
        <section id="devis" className="quote" data-testid="quote-section">
          <div className="section-header">
            <h2 className="section-title" data-testid="quote-title">DEMANDER UN DEVIS</h2>
            <p className="section-subtitle">Confiez-nous votre vision, nous créerons un spectacle d'exception sur mesure</p>
          </div>

          <form onSubmit={handleQuoteSubmit} className="quote-form" data-testid="quote-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote-name">Nom complet *</label>
                <input
                  id="quote-name"
                  type="text"
                  value={quoteForm.name}
                  onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                  required
                  data-testid="quote-name-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-email">Email *</label>
                <input
                  id="quote-email"
                  type="email"
                  value={quoteForm.email}
                  onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                  required
                  data-testid="quote-email-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote-phone">Téléphone *</label>
                <input
                  id="quote-phone"
                  type="tel"
                  value={quoteForm.phone}
                  onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                  required
                  data-testid="quote-phone-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-event-type">Type d'événement *</label>
                <select
                  id="quote-event-type"
                  value={quoteForm.event_type}
                  onChange={(e) => setQuoteForm({ ...quoteForm, event_type: e.target.value })}
                  required
                  data-testid="quote-event-type-select"
                >
                  <option value="Mariage">Mariage</option>
                  <option value="Commune">Commune / Municipalité</option>
                  <option value="Événementiel">Événement d'entreprise</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote-event-date">Date de l'événement *</label>
                <input
                  id="quote-event-date"
                  type="date"
                  value={quoteForm.event_date}
                  onChange={(e) => setQuoteForm({ ...quoteForm, event_date: e.target.value })}
                  required
                  data-testid="quote-event-date-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-location">Lieu *</label>
                <input
                  id="quote-location"
                  type="text"
                  value={quoteForm.location}
                  onChange={(e) => setQuoteForm({ ...quoteForm, location: e.target.value })}
                  placeholder="Ville, région"
                  required
                  data-testid="quote-location-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="quote-budget">Budget estimé</label>
              <select
                id="quote-budget"
                value={quoteForm.budget}
                onChange={(e) => setQuoteForm({ ...quoteForm, budget: e.target.value })}
                data-testid="quote-budget-select"
              >
                <option value="">Sélectionner...</option>
                <option value="Moins de 2000€">Moins de 2000€</option>
                <option value="2000€ - 5000€">2000€ - 5000€</option>
                <option value="5000€ - 10000€">5000€ - 10000€</option>
                <option value="Plus de 10000€">Plus de 10000€</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="quote-description">Description de votre projet *</label>
              <textarea
                id="quote-description"
                value={quoteForm.description}
                onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })}
                placeholder="Décrivez-nous votre vision, vos attentes, le type de spectacle souhaité..."
                rows="5"
                required
                data-testid="quote-description-textarea"
              ></textarea>
            </div>

            <button type="submit" className="form-submit" data-testid="quote-submit-button">
              <Send className="submit-icon" />
              Envoyer ma demande
            </button>

            {submitStatus.quote && (
              <div className={`form-status ${submitStatus.quote.includes('Erreur') ? 'error' : 'success'}`} data-testid="quote-submit-status">
                {submitStatus.quote}
              </div>
            )}
          </form>
        </section>

        {/* Contact Section */}
        <ContactSection />

        {/* Footer */}
        <footer className="footer" data-testid="footer">
          <div className="footer-content">
            <div className="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_cyga-refresh/artifacts/iagmxpji_Cyga%20events%20Visuel%20Site-02.png" 
                alt="Cyga Events Logo" 
                className="logo-icon-img"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_cyga-refresh/artifacts/pw7229o4_Cyga%20events%20Visuel%20Site-04.png" 
                alt="Cyga Events" 
                className="logo-text-img"
              />
            </div>
            <p className="footer-text">
              Plus de 30 ans d'expérience dans l'artifice pour du spectacle unique et inoubliable.
            </p>
            <div className="footer-links">
              <button onClick={() => scrollToSection('accueil')}>Accueil</button>
              <button onClick={() => scrollToSection('services')}>Services</button>
              <button onClick={() => scrollToSection('galerie')}>Galerie</button>
              <button onClick={() => navigate('/blog')}>Blog</button>
              <button onClick={() => scrollToSection('contact')}>Contact</button>
            </div>
            <p className="footer-copyright">
              © 2025 CYGA EVENEMENT. Tous droits réservés.
            </p>
            <button 
              onClick={() => navigate('/admin')} 
              className="admin-access-button"
              title="Accès Administration"
            >
              Admin
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

export default Home;
